import { useLayoutEffect, useState } from 'react';

import { useTheme } from '@mui/material';

import {
  IconButton,
  Icons,
  TextArea,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

const isTextAreaMultiline = (
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
) => {
  if (!textareaRef.current?.value) {
    return false;
  }

  const computedStyle = window.getComputedStyle(textareaRef.current);
  const lineHeight = parseFloat(computedStyle.lineHeight);
  const height = textareaRef.current.scrollHeight;
  const numberOfLines = Math.round(height / lineHeight);

  return numberOfLines > 1;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  onSendMessage: () => void;
  loadingResponse: boolean;
  ref: React.RefObject<HTMLTextAreaElement | null>;
  maxLength?: number;
};

export const ChatInput = ({
  value,
  onChange,
  onSendMessage,
  loadingResponse,
  ref,
  maxLength,
}: Props) => {
  const { breakpoints } = useTheme();

  const isOverLimit = maxLength ? value.length > maxLength : false;
  const showCounter = maxLength ? value.length > maxLength * 0.9 : false;
  const [isMultiline, setIsMultiline] = useState(false);

  useLayoutEffect(() => {
    setIsMultiline(isTextAreaMultiline(ref));
  }, [ref, value]);

  const counterId = 'chat-input-char-counter';

  return (
    <Stack
      border="1px solid"
      borderColor={tokens.theme.border.structural.neutral}
      borderRadius="4px"
      p={12}
      width="100%"
      direction="row"
      alignItems="center"
      display="grid"
      gridTemplateColumns="1fr auto"
      columnGap="8px"
      onClick={() => {
        ref.current?.focus();
      }}
    >
      <TextArea
        inputRef={ref}
        responsive
        minRows={1}
        maxRows={4}
        data-testid="chat-input-textarea"
        inputProps={{
          'aria-describedby':
            maxLength !== undefined && showCounter ? counterId : undefined,
          'aria-invalid': isOverLimit,
          className: 'DS-disableResize',
        }}
        InputProps={{
          sx: {
            outline: 'none !important',
            border: 'none !important',
            boxShadow: 'none !important',

            '& .MuiInputBase-input': {
              fontSize: '16px',
              [`@media (min-width: ${breakpoints.values.laptop}px)`]: {
                fontSize: '14px',
              },
              py: '0px !important',
              px: '0px !important',
              minHeight: '18px !important',
              backgroundColor: 'transparent !important',
              '&::placeholder': {
                color: tokens.theme.fg.structural.secondary,
              },
            },
          },
        }}
        placeholder="Ask your question"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loadingResponse}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();

            if (!value?.trim() || isOverLimit) {
              return;
            }

            onSendMessage();
          }
        }}
      />
      <IconButton
        sx={{
          gridRow: isMultiline ? 2 : 1,
          gridColumn: 2,
        }}
        size="sm"
        variant="primary"
        onClick={(e) => {
          e.stopPropagation();
          onSendMessage();
        }}
        disabled={loadingResponse || !value?.trim() || isOverLimit}
        data-testid="chat-send-button"
      >
        <Icons.ArrowUp />
      </IconButton>
      {maxLength !== undefined && showCounter && (
        <Typography
          id={counterId}
          variant="P10R"
          color={tokens.theme.fg.status.error}
          aria-live="polite"
          aria-atomic="true"
          role="status"
          gridColumn="1 / -1"
          data-testid="chat-input-char-counter"
        >
          {value.length} / {maxLength}
        </Typography>
      )}
    </Stack>
  );
};
